import React, {useEffect, useRef, useState} from 'react'
import {Checkbox, Col, Form, Input, message, Row, Select, Space, Spin, Tooltip, Typography, Upload} from 'antd'
import {ArrowUpOutlined} from '@ant-design/icons'
import ColorTypeSelect from './ColorTypeSelect'
import FormErrorMessage from '../base/FormErrorMessage'
import {validateMessages} from '../../app/util'
import {uploadImage} from '../../api/amazonS3'
import {processImage} from '../../api/colorAutoTagging'
import HttpError from '../base/HttpError'
import {initMultipleSelect, RequiredTitle} from './common'
import TextArea from 'antd/es/input/TextArea'
import NewPiecesFormButton from './NewPiecesFormButton'
import {loadForm} from '../../app/cacheUtil'
import {is_admin_user} from "../../app/role";

const {Title, Text} = Typography

export default (props) => {
    const [email, setEmail] = useState(props.email)
    const [validateError, setValidateError] = useState([])
    const [colorTaggingLoading, setColorTaggingLoading] = useState(false)
    const [dimsDesc, setDimsDesc] = useState(false)
    const [colorTaggingConfirmVisible, setColorTaggingConfirmVisible] = useState(false)
    const [uploadingImage, setUploadingImage] = useState(false)
    const formRef = useRef(null)

    const onFinishFailed = ({values, errorFields, outOfDate}) => {
        setValidateError(errorFields)
        window.scrollTo(0, 0)
        window.scrollTop = 0
    }

    const customRequest = (options) => {
        const {onError, onSuccess, file, onProgress} = options
        if (!email) {
            message.error("Please select an artist before uploading any images.").then(()=>{})
            onError("Please select an artist before uploading any images.")
            return
        }
        setUploadingImage(true)

        const myOnSuccess = (data) => {
            onSuccess(data)
            setUploadingImage(false)
        }
        const myOnError = (err) => {
            onError(err)
            setUploadingImage(false)
        }
        uploadImage(email, file, myOnSuccess, myOnError, onProgress)
    }

    const customRequestOnChange = (e) => {
        const {file} = e
        if (file && file.status === 'done' && file.response) {
            setColorTaggingLoading(true)
            processImage(file.response.static_url)
                .then((res) => {
                    let color_str = res.join(',')
                    color_str = color_str.toUpperCase()
                    setColorTaggingConfirmVisible(true)
                    formRef.current?.setFieldsValue({
                        color_info_str: initMultipleSelect(color_str)
                    })
                })
                .catch((e) => {
                    HttpError(e)
                })
                .finally(() => {
                    setColorTaggingLoading(false)
                })
        }
    }

    useEffect(() => {
        const cache = loadForm(props.formId)
        if (cache) {
            formRef.current?.setFieldsValue(cache)
        }
        const default_artist = getDefaultSelectedArtist()
        if (default_artist) {
            if (cache) {
                formRef.current?.setFieldsValue({...cache, artist: default_artist.id})
            } else {
                formRef.current?.setFieldsValue({artist: default_artist.id})
            }
        }
    }, [formRef?.current])

    const getDefaultSelectedArtist = () => {
        if (props.artistId && props.artists) {
            const selected = props.artists.filter((artist) => (artist.artist_id.toString() === props.artistId.toString()))
            if (selected.length > 0) return selected[0]
            return null
        }
        return null
    }
    return (
        <>
            <FormErrorMessage error={validateError}/>
            <Form
                colon={false}
                ref={formRef}
                labelAlign='left'
                layout={'vertical'}
                onFinish={props.onFinish}
                validateMessages={validateMessages}
                onFinishFailed={onFinishFailed}
                style={{
                    marginTop: '16px'
                }}
            >
                <Row
                    style={{
                        paddingLeft: '3vw',
                        paddingRight: '3vw',
                        maxWidth: 1280,
                        height: '100%',
                        flexDirection: 'row'
                    }}
                >
                    <Col xs={24} sm={24} md={12} style={{marginTop: 32, paddingLeft: 18, paddingRight: 18}}>
                        <Space direction={'vertical'}>
                            <div style={{height: '66vh'}}>
                                <Form.Item
                                    name='image'
                                    label='The Main Image'
                                    valuePropName='fileList'
                                    getValueFromEvent={(e) => {
                                        return e && e.fileList
                                    }}
                                    rules={[
                                        {
                                            required: true
                                        }
                                    ]}
                                    noStyle
                                >
                                    <Upload.Dragger
                                        accept='image/*'
                                        listType='picture'
                                        customRequest={customRequest}
                                        onChange={customRequestOnChange}
                                        maxCount={1}
                                    >
                                        <div className='uploadDraggerContainer'>
                                            <Text style={{alignSelf: 'flex-start'}}>
                                                Upload a high resolution image of your art. Your image must be 1MB and
                                                less than 50 MB.
                                            </Text>
                                            <br/>
                                            <Text type='secondary' style={{alignSelf: 'flex-start'}}>
                                                Recommendation: Use high quality jpeg images less than 20MB
                                            </Text>
                                        </div>
                                    </Upload.Dragger>
                                </Form.Item>
                                <Text style={{float: 'right', padding: 1}}>
                                    <span style={{color: 'red'}}>*</span>(Required)
                                </Text>
                            </div>
                            <Form.Item
                                name='additional_image'
                                valuePropName='fileList'
                                getValueFromEvent={(e) => {
                                    return e && e.fileList
                                }}
                                rules={[
                                    {
                                        required: false
                                    }
                                ]}
                            >
                                <Upload.Dragger accept='image/*' listType='picture' customRequest={customRequest}
                                                maxCount={4}>
                                    <div className='uploadDraggerContainer'>
                                        <Text type='primary' style={{alignSelf: 'middle'}}>
                                            <ArrowUpOutlined/> Additional Images
                                        </Text>
                                    </div>
                                </Upload.Dragger>
                            </Form.Item>
                        </Space>
                    </Col>
                    <Col xs={24} sm={24} md={12} style={{marginTop: 32, paddingLeft: 18, paddingRight: 18}}>
                        {is_admin_user(props) && !props.artistId && (
                            <Form.Item name='artist' label={RequiredTitle('Artist')} rules={[{required: true}]}>
                                <Select
                                    showSearch
                                    optionFilterProp='label'
                                    placeholder='Please select artist'
                                    onSelect={(e)=>{
                                        const selected_artist = props.artists.filter((artist)=>{
                                            return artist.artist_id.toString() === e.toString()
                                        })
                                        if (selected_artist.length > 0) {
                                            setEmail(selected_artist[0].email)
                                        }
                                    }}
                                    options={props.artists?.map((artist) => ({
                                        value: artist.artist_id,
                                        label: artist.name
                                    }))}
                                    disabled={!props.new}
                                />
                            </Form.Item>
                        )}
                        <Form.Item
                            name='name'
                            label={RequiredTitle('Title')}
                            style={{paddingBottom: 4}}
                            rules={[
                                {
                                    required: true,
                                    type: 'string',
                                    max: 100,
                                    min: 1
                                }
                            ]}
                        >
                            <Input/>
                        </Form.Item>
                        <Tooltip title='Our AI auto tagged the color for you' visible={colorTaggingConfirmVisible}>
                            <Spin size={'small'} spinning={colorTaggingLoading}>
                                <Form.Item style={{height: 1, margin: 0, marginLeft: 15}}/>
                            </Spin>
                        </Tooltip>

                        <Form.Item
                            name='color_info_str'
                            label={
                                <>
                                    <Text>
                                        Color <span style={{fontSize: 12, color: 'gray'}}>(Auto-tagging)</span>
                                    </Text>
                                </>
                            }
                            rules={[
                                {
                                    required: false
                                }
                            ]}
                        >
                            <ColorTypeSelect/>
                        </Form.Item>
                        <Space direction={'vertical'} size={'large'}>
                            <Form.Item noStyle>
                                <Text>
                                    Dimensions of work (in inches) <span style={{fontSize: 12, color: 'gray'}}><span
                                    style={{color: 'red'}}>*</span>(Required)</span>
                                </Text>
                                <br/>
                                <br/>
                                <Text type='secondary'>For flat artworks, such as works on paper, we suggest that you
                                    enter a depth of 0.1</Text>
                                <br/>
                                <Text type='secondary'>Use decimals when needed (eg. 10.5 instead of 10 1/2)</Text>
                            </Form.Item>
                            {!dimsDesc && (
                                <Space>
                                    <Form.Item
                                        label='Height'
                                        name='height'
                                        initialValue={''}
                                        rules={[
                                            {
                                                required: false,
                                                type: 'number',
                                                transform: (value) => {
                                                    return Number(value)
                                                }
                                            }
                                        ]}
                                        noStyle
                                    >
                                        <Input prefix={<Text>H</Text>}/>
                                    </Form.Item>
                                    <Form.Item
                                        label='Width'
                                        name='width'
                                        initialValue={''}
                                        rules={[
                                            {
                                                required: false,
                                                type: 'number',
                                                transform: (value) => {
                                                    return Number(value)
                                                }
                                            }
                                        ]}
                                        noStyle
                                    >
                                        <Input prefix={<Text>W</Text>}/>
                                    </Form.Item>
                                    <Form.Item
                                        label='Depth'
                                        name='depth'
                                        initialValue={''}
                                        rules={[
                                            {
                                                required: false,
                                                type: 'number',
                                                transform: (value) => {
                                                    return Number(value)
                                                }
                                            }
                                        ]}
                                        noStyle
                                    >
                                        <Input prefix={<Text>D</Text>}/>
                                    </Form.Item>
                                </Space>
                            )}
                            <Form.Item name='additional_dims_desc' label='Framed' valuePropName='checked' noStyle>
                                <Checkbox
                                    style={{marginBottom: 12, marginLeft: 0}}
                                    onChange={(e) => {
                                        setDimsDesc(e.target.checked)
                                    }}
                                >
                                    I want to have additional dimension description
                                </Checkbox>
                            </Form.Item>
                            {dimsDesc && (
                                <>
                                    <Form.Item name='size_desc_str' noStyle>
                                        <TextArea
                                            autoSize={{
                                                minRows: 4,
                                                maxRows: 6
                                            }}
                                        />
                                    </Form.Item>
                                </>
                            )}
                            <Form.Item name='is_framed' label='Framed' valuePropName='checked' noStyle>
                                <Checkbox style={{marginBottom: 12, marginLeft: 0}}>Framed - Artwork size with
                                    frame</Checkbox>
                            </Form.Item>
                        </Space>
                    </Col>
                    <NewPiecesFormButton {...props} stepperCount={0} actionLoading={uploadingImage} />
                </Row>
            </Form>
        </>
    )
}
